import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Key,
  FileText,
  DollarSign,
  ClipboardCheck,
  ChevronDown,
} from "lucide-react";

const steps = {
  buying: [
    {
      id: 1,
      title: "Browse Properties",
      description: "Explore our luxury developments and find your dream home.",
      icon: Home,
    },
    {
      id: 2,
      title: "Schedule a Tour",
      description: "Visit our properties in person with one of our consultants.",
      icon: Key,
    },
    {
      id: 3,
      title: "Payment Plan",
      description:
        "Choose from flexible financing options tailored to your lifestyle.",
      icon: DollarSign,
    },
    {
      id: 4,
      title: "Ownership Transfer",
      description: "Sign agreements and complete the transfer seamlessly.",
      icon: FileText,
    },
  ],
  renting: [
    {
      id: 1,
      title: "Choose Property",
      description: "Select your preferred apartment, penthouse, or estate unit.",
      icon: Home,
    },
    {
      id: 2,
      title: "Review Agreement",
      description: "Understand rental terms, conditions, and required documents.",
      icon: ClipboardCheck,
    },
    {
      id: 3,
      title: "Submit Documents",
      description: "Provide IDs, proof of income, and required deposits.",
      icon: FileText,
    },
    {
      id: 4,
      title: "Move In",
      description: "Receive your keys and step into your new lifestyle.",
      icon: Key,
    },
  ],
};

const faqs = [
  {
    q: "Can foreigners buy property?",
    a: "Yes, Aspen Plush Homes properties are available for both local and international buyers, with full ownership rights.",
  },
  {
    q: "What documents are required?",
    a: "You'll need valid identification, proof of income or financing, and in some cases, a tax clearance certificate.",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes, we provide flexible financing and installment options tailored to your needs.",
  },
  {
    q: "Is renting fully furnished?",
    a: "Most of our rental properties come fully furnished, but you can also opt for unfurnished units.",
  },
];

export default function HowToBuy() {
  const [mode, setMode] = useState("buying");
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="bg-gray-50">
      {/* Hero */}
      <section
        className="relative h-[50vh] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: "url('/luxeheights.jpg')" }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <motion.div
          className="relative z-10 text-center text-white px-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
            How to Buy or Rent
          </h1>
          <p className="text-lg max-w-2xl mx-auto">
            Discover how simple it is to secure your dream property with Aspen
            Plush Homes.
          </p>
        </motion.div>
      </section>

      {/* Mode Switch */}
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <div className="inline-flex bg-white rounded-xl shadow p-2">
          <button
            className={`px-6 py-2 rounded-lg font-medium transition ${
              mode === "buying"
                ? "bg-indigo-600 text-white shadow"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setMode("buying")}
          >
            Buying
          </button>
          <button
            className={`px-6 py-2 rounded-lg font-medium transition ${
              mode === "renting"
                ? "bg-indigo-600 text-white shadow"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setMode("renting")}
          >
            Renting
          </button>
        </div>
      </div>

      {/* Steps Timeline */}
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="relative border-l-4 border-indigo-100 pl-6">
          {steps[mode].map((step, index) => (
            <motion.div
              key={step.id}
              className="mb-12 relative"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              {/* Icon */}
              <div className="absolute -left-11 top-1 w-10 h-10 flex items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg">
                <step.icon className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition">
                <h3 className="text-lg font-semibold text-gray-900">
                  {step.title}
                </h3>
                <p className="text-gray-600 mt-2">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-xl shadow-sm bg-gray-50"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                  className="w-full flex justify-between items-center px-6 py-4 text-left font-medium text-gray-800"
                >
                  {faq.q}
                  <motion.div
                    animate={{ rotate: openFAQ === idx ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openFAQ === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="px-6 pb-4 text-gray-600"
                    >
                      {faq.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
